import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Quote, MapPin, Globe, Users, ExternalLink } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ContactForm from "@/components/ContactForm";
import Breadcrumbs from "@/components/Breadcrumbs";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const viewportOnce = { once: true, amount: 0.05 };

const STUDIES = {
  "cavern-club": {
    name: "The Cavern Club",
    tagline: "The Most Famous Club in the World",
    industry: "Live Music & Hospitality",
    location: "10 Mathew Street, Liverpool",
    website: "https://www.cavernclub.com",
    heroImg: "https://cdn.candopayments.com/case-studies/cavern-club_hero_colour_web_solid.jpeg",
    about: "The Cavern Club is the birthplace of The Beatles and the most famous music venue on the planet. Since 1957, it has hosted over 300 performances by The Beatles alone and continues to present live music every day of the week. The venue includes multiple bars, a restaurant, a merchandise shop, The Cavern Pub, and the award-winning Live Lounge — plus the iconic Magical Mystery Tour bus experience.",
    stats: [
      { value: "1957", label: "Established" },
      { value: "5+", label: "Venue Spaces" },
      { value: "365", label: "Days of Live Music" },
      { value: "Global", label: "Tourist Destination" },
    ],
    challenge: "With five interconnected venue spaces operating simultaneously — including multiple bars, a restaurant, a retail shop, and ticketed live events — The Cavern Club needed a payment solution that could handle extraordinary footfall, especially during peak events like International Beatleweek (winner of Festival of the Year). Legacy terminals were slow, unreliable during peaks, and offered no real-time visibility across the operation. Every second of queue time meant a worse experience for visitors who'd travelled from around the world.",
    solution: "CanDo Payments deployed a fully integrated payment ecosystem across every touchpoint in The Cavern Club. Fast contactless terminals were installed at every bar, the restaurant, and the merchandise shop. Real-time reporting gave management instant visibility of sales performance across all five spaces — from the original Cavern stage to the Live Lounge. The system was configured to handle the extreme spikes that come with hosting globally famous events, with redundancy built in so no terminal ever goes down during a set.",
    results: [
      "Sub-2-second contactless payments across all 5 venue spaces",
      "Real-time sales dashboard covering bars, restaurant, retail and events",
      "Zero payment downtime during International Beatleweek (20,000+ visitors)",
      "Next-day settlement keeping cash flow strong for a 365-day operation",
      "Significant reduction in processing costs vs. previous provider",
      "24/7 UK-based support for a venue that never sleeps",
    ],
    quote: "CanDo Payments understood the unique demands of our venue. The support has been fantastic — they're always on the end of the phone when we need them. The system handles our busiest nights without breaking a sweat.",
    quotee: "Operations Manager, The Cavern Club",
  },
  "gladstones-library": {
    name: "Gladstone's Library",
    tagline: "The UK's Finest Residential Library, Since 1894",
    industry: "Heritage, Hospitality & Culture",
    location: "Hawarden, Flintshire",
    website: "https://www.gladstoneslibrary.org",
    heroImg: "https://cdn.candopayments.com/case-studies/gladstones-library_hero_colour_web_solid.jpeg",
    about: "Gladstone's Library is a place unlike anywhere else in the world. The UK's only Prime Ministerial library, founded following a bequest from four-times Prime Minister William Ewart Gladstone, it houses world-famous Reading Rooms with thousands of books in a stunning Grade I listed building designed by John Douglas in 1902. But it's also a 26-bedroom hotel, an award-winning restaurant (Food for Thought), a conference venue, and a destination for writers, scholars, and thinkers from across the globe. It is a self-sustaining registered charity — every booking helps preserve this extraordinary building and its collections.",
    stats: [
      { value: "1894", label: "Founded" },
      { value: "26", label: "Bedrooms" },
      { value: "250k+", label: "Books & Journals" },
      { value: "Grade I", label: "Listed Building" },
    ],
    challenge: "Gladstone's Library operates as a uniquely complex business — part residential hotel, part public restaurant, part conference venue, part library and research centre, part events programme. They needed a payment system flexible enough to handle accommodation deposits, restaurant bills, event ticket sales, Reading Room fees, and gift shop purchases — all under one historic roof. Previous systems were fragmented, with different solutions for different parts of the business creating reconciliation headaches and a disjointed guest experience.",
    solution: "CanDo Payments implemented a unified payment system that brought every revenue stream together. Integrated card machines were deployed across accommodation check-in, the Food for Thought restaurant, the events desk, and the gift shop. Pay by Link was set up for advance room bookings and event deposits — reducing no-shows and improving cash flow. Online payment integration was added for their growing events programme, including writing courses and literary weekends.",
    results: [
      "Single unified payment system across hotel, restaurant, events and retail",
      "Pay by Link for advance bookings — reducing no-shows significantly",
      "Simplified reporting across all revenue streams in one dashboard",
      "Seamless guest experience from room booking to restaurant bill",
      "Online payment integration for events and writing courses",
      "Ongoing support and system updates as the charity grows",
    ],
    quote: "CanDo took the time to understand how our business works — and it's not a straightforward one. The system they put together handles everything seamlessly, from room bookings to restaurant bills to event tickets. It just works.",
    quotee: "Business Manager, Gladstone's Library",
  },
  "krcs": {
    name: "KRCS",
    tagline: "Apple Premium Reseller Since 1984",
    industry: "Premium Retail & E-Commerce",
    location: "Multiple UK Locations",
    website: "https://www.krcs.co.uk",
    heroImg: "https://cdn.candopayments.com/case-studies/krcs_hero_colour_web_solid.webp",
    about: "KRCS is one of the UK's longest-established Apple Premium Resellers, operating since 1984. They stock the full range of Apple products — Mac, MacBook, MacBook Pro, iPad, iPhone, and accessories — with expert advice and premium customer service that matches the Apple brand standard. With both retail stores and a thriving e-commerce operation offering free next-working-day delivery on in-stock items ordered before 3pm, KRCS serves customers who expect nothing less than exceptional.",
    stats: [
      { value: "1984", label: "Established" },
      { value: "40+", label: "Years Trading" },
      { value: "Apple", label: "Premium Reseller" },
      { value: "UK-Wide", label: "Retail & Online" },
    ],
    challenge: "As an Apple Premium Reseller, KRCS handles high-value transactions daily — MacBook Pro orders can exceed £3,000. Their payment solution needed to match the premium experience their customers expected: fast, secure, and utterly reliable. With both physical retail and a busy online store, they needed a provider that could handle in-store contactless, Apple Pay, and high-value card-not-present transactions — all with the security and support that a premium brand demands.",
    solution: "CanDo Payments provided fast, secure payment processing across all KRCS retail locations and their e-commerce platform. The setup included contactless and all digital wallets as standard, with enhanced security protocols for high-value transactions. A payment gateway was integrated into their online store for seamless checkout. Next-day settlement was configured to keep cash flow healthy across a business with significant stock value, and a dedicated account manager was assigned for ongoing support.",
    results: [
      "Fast, reliable payment processing matching the Apple brand standard",
      "Enhanced security for high-value transactions (£1,000+)",
      "Contactless, Apple Pay, and Google Pay across all stores",
      "Seamless payment gateway integration for the e-commerce platform",
      "Dedicated account management for premium-level support",
      "Transparent pricing with significant savings over their previous provider",
    ],
    quote: "We needed a payment partner who could deliver the same quality our customers expect from Apple. CanDo Payments delivered exactly that — reliable, fast, and with a level of support that feels genuinely personal.",
    quotee: "Director, KRCS",
  },
  "thorncliffe": {
    name: "Thorncliffe Building Supplies",
    tagline: "North Wales' Largest Independent Builder's Merchant",
    industry: "Building Supplies, Plant Hire & Retail",
    location: "North Wales & North West England",
    website: "https://www.thorncliffebs.co.uk",
    heroImg: "https://cdn.candopayments.com/case-studies/thorncliffe_colour_web_solid.jpg",
    about: "Thorncliffe Building Supplies is one of the largest independent builder's merchants in North Wales and the North West of England. Established in 1987, they operate from multiple branches in Dyserth, Ewloe, and Wrexham — plus a thriving online shop, skip hire service, plant and tool hire, groundcare machinery, event power solutions, and even a Can-Am dealership. Their reputation for outstanding customer service is legendary — customers routinely say they feel like they've spent £28,000 when they've only spent £28.",
    stats: [
      { value: "1987", label: "Established" },
      { value: "5+", label: "Branches" },
      { value: "37+", label: "Years Trading" },
      { value: "1000s", label: "Trade Accounts" },
    ],
    challenge: "Thorncliffe's operation is vast and varied — from trade counter sales and bulk material deliveries to online ordering, skip hire bookings, and plant hire deposits. With multiple branches processing hundreds of transactions daily, they needed a payment system that could keep up with trade-counter speed, integrate across locations, and provide clear reporting for a business that spans retail, e-commerce, hire, and waste management. Legacy terminals were slowing down service at the busiest times.",
    solution: "CanDo Payments replaced Thorncliffe's legacy terminals with modern smart devices across all branches. Integrated EPOS was set up for trade counter management, giving staff instant access to stock, pricing and account information. Online payment integration was added for their e-commerce store, and card-on-file was configured for skip hire and plant hire bookings. Real-time reporting now gives management visibility across every branch and every revenue stream from a single dashboard.",
    results: [
      "Modern smart terminals across all 5+ branches — no more slow legacy equipment",
      "Integrated EPOS for trade counter operations",
      "Online payment integration for e-commerce and hire bookings",
      "Real-time multi-branch reporting from a single dashboard",
      "Reduced processing costs with transparent, honest pricing",
      "Fast, responsive support from a dedicated UK team",
    ],
    quote: "The difference since switching to CanDo has been night and day. Everything just works, the team actually picks up the phone, and our trade customers are through the till faster than ever. It's the level of service we try to give our own customers — and now we get it from our payment provider too.",
    quotee: "Management Team, Thorncliffe Building Supplies",
  },
};

function StatCard({ stat }) {
  return (
    <div className="text-center">
      <p className="text-2xl sm:text-3xl font-heading font-bold text-brand-aqua">{stat.value}</p>
      <p className="text-xs text-brand-grey uppercase tracking-wider mt-1">{stat.label}</p>
    </div>
  );
}

function ResultItem({ text }) {
  return (
    <div className="flex items-start gap-3 text-sm text-brand-black">
      <CheckCircle size={18} className="text-brand-aqua flex-shrink-0 mt-0.5" />{text}
    </div>
  );
}

export default function CaseStudyPage() {
  const { study } = useParams();
  const data = STUDIES[study];
  if (!data) return <Navigate to="/case-studies" replace />;

  return (
    <div>
      <SEOHead title={`${data.name} — Case Study`} description={`How CanDo Payments helped ${data.name} transform their payment operations.`} path={`/case-studies/${study}`} />

      {/* Hero with background image */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid={`cs-${study}-hero`}>
        <div className="absolute inset-0">
          <img src={data.heroImg} alt={data.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black/90 via-brand-black/75 to-brand-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl text-white">
            <Breadcrumbs variant="dark" className="mb-5" labels={{ [study]: data.name }} />
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-brand-aqua text-white text-xs font-bold px-3 py-1 rounded-full">{data.industry}</span>
              <span className="flex items-center gap-1.5 text-sm text-white/70"><MapPin size={14} />{data.location}</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-3">{data.name}</motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-white/60 font-semibold mb-6">{data.tagline}</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a href={data.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
                <Globe size={14} />{`Visit ${data.name}`}<ExternalLink size={12} />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-8 bg-brand-black text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {data.stats.map((s) => <StatCard key={s.label} stat={s} />)}
        </div>
      </section>

      {/* About the business */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">{`About ${data.name}`}</motion.p>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed">{data.about}</motion.p>
          </motion.div>
        </div>
      </section>

      {/* Challenge + Solution */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-heading text-2xl sm:text-3xl font-bold text-brand-black mb-4">The Challenge</motion.h2>
            <motion.p variants={fadeUp} className="text-brand-grey leading-relaxed">{data.challenge}</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-heading text-2xl sm:text-3xl font-bold text-brand-black mb-4">Our Solution</motion.h2>
            <motion.p variants={fadeUp} className="text-brand-grey leading-relaxed">{data.solution}</motion.p>
          </motion.div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-heading text-2xl sm:text-3xl font-bold text-brand-black mb-8">The Results</motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.results.map((r) => (
                <motion.div key={r} variants={fadeUp}><ResultItem text={r} /></motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 md:py-20 bg-brand-black text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Quote size={32} className="text-brand-aqua mx-auto mb-6" />
          <p className="text-xl sm:text-2xl font-heading leading-relaxed mb-6">{`“${data.quote}”`}</p>
          <p className="text-sm text-brand-grey">{`— ${data.quotee}`}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Your Business Could Be Next</p>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Ready to see what CanDo can do for you?</h2>
            <p className="text-brand-grey leading-relaxed mb-6">Whether you run a world-famous music venue, a historic library, a premium retail chain, or a multi-branch merchant — we build payment solutions around how your business actually works.</p>
            <div className="space-y-3">
              {["Transparent pricing — no hidden fees", "Real human support — not a chatbot", "Systems designed around your specific operation", "Next-day settlement as standard"].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                  <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Link to="/case-studies" className="text-brand-aqua font-semibold text-sm hover:underline flex items-center gap-1">
                View all case studies <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Talk to us about your business</h3>
            <p className="text-sm text-brand-grey mb-4">Tell us what you need and we&apos;ll show you how we can help.</p>
            <ContactForm context={{ caseStudy: data.name, industryLabel: data.industry }} />
          </div>
        </div>
      </section>
    </div>
  );
}
